import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { JourneyTimeline } from './JourneyTimeline';
import { MapPin, Bus, Tablet, Gamepad2, Play, Trophy, Navigation, Car } from 'lucide-react';
const container = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const item = {
  hidden: {
    opacity: 0,
    x: -20
  },
  show: {
    opacity: 1,
    x: 0
  }
};
function UberPhoneMockup({
  variant
}: {
  variant: 'waiting' | 'ad' | 'engaged';
}) {
  return <div className="relative bg-[#1a1a1a] rounded-[24px] p-[5px] shadow-2xl w-[160px] shrink-0" data-id="element-77">
      <div className="absolute top-[5px] left-1/2 -translate-x-1/2 w-16 h-4 bg-[#1a1a1a] rounded-b-lg z-20" data-id="element-78" />
      <div className="bg-white rounded-[20px] overflow-hidden" data-id="element-79">
        {variant === 'waiting' && <>
            {/* Uber-style header */}
            <div className="bg-black px-3 py-1.5 pt-6 flex items-center justify-between" data-id="element-80">
              <div className="flex items-center gap-1" data-id="element-81">
                <span className="text-white text-[10px] font-bold" data-id="element-82">Uber</span>
              </div>
              <div className="flex items-center gap-1" data-id="element-83">
                <Navigation className="w-2.5 h-2.5 text-green-400" data-id="element-84" />
                <span className="text-green-400 text-[7px] font-medium" data-id="element-85">
                  Live
                </span>
              </div>
            </div>
            {/* Map area */}
            <div className="relative bg-[#E8E4DA] h-[90px] overflow-hidden" data-id="element-86">
              {/* Stylized map grid */}
              <div className="absolute inset-0 opacity-30" data-id="element-87">
                <div className="absolute top-4 left-3 right-6 h-[1px] bg-gray-400" data-id="element-88" />
                <div className="absolute top-8 left-8 right-3 h-[1px] bg-gray-400" data-id="element-89" />
                <div className="absolute top-12 left-2 right-10 h-[1px] bg-gray-400" data-id="element-90" />
                <div className="absolute top-3 left-6 w-[1px] h-16 bg-gray-400" data-id="element-91" />
                <div className="absolute top-2 left-12 w-[1px] h-20 bg-gray-400" data-id="element-92" />
                <div className="absolute top-6 right-8 w-[1px] h-14 bg-gray-400" data-id="element-93" />
              </div>
              {/* Congress venue pin */}
              <div className="absolute top-6 right-5 flex flex-col items-center" data-id="element-94">
                <div className="bg-[#E60012] rounded-full w-5 h-5 flex items-center justify-center shadow-md" data-id="element-95">
                  <MapPin className="w-3 h-3 text-white" data-id="element-96" />
                </div>
                <div className="bg-white rounded px-1 py-0.5 mt-0.5 shadow-sm" data-id="element-97">
                  <span className="text-[5px] font-bold text-[#2D2926]" data-id="element-98">
                    Congress
                  </span>
                </div>
              </div>
              {/* Geo-fence radius */}
              <div className="absolute top-2 right-1 w-14 h-14 rounded-full border-2 border-[#E60012]/25 bg-[#E60012]/5" data-id="element-99" />
              {/* Car icon */}
              <div className="absolute bottom-3 left-4" data-id="element-100">
                <div className="bg-black rounded-full w-4 h-4 flex items-center justify-center" data-id="element-101">
                  <Car className="w-2.5 h-2.5 text-white" data-id="element-102" />
                </div>
              </div>
              {/* Route line */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 160 90" data-id="element-103">
                <path d="M 25 72 Q 60 50 80 40 Q 110 25 125 30" stroke="#276EF1" strokeWidth="2" fill="none" strokeDasharray="4 3" data-id="element-104" />
              </svg>
            </div>
            {/* Ride info */}
            <div className="bg-white px-3 py-2 border-t border-gray-100" data-id="element-105">
              <div className="flex items-center justify-between mb-1" data-id="element-106">
                <span className="text-[9px] font-bold text-black" data-id="element-107">
                  UberX arriving
                </span>
                <span className="text-[9px] font-bold text-black" data-id="element-108">3 min</span>
              </div>
              <div className="h-1 bg-gray-100 rounded-full overflow-hidden" data-id="element-109">
                <div className="h-full w-2/3 bg-black rounded-full" data-id="element-110" />
              </div>
              <p className="text-[7px] text-gray-500 mt-1" data-id="element-111">
                Heading to Congress Center
              </p>
            </div>
          </>}

        {variant === 'ad' && <>
            {/* Uber header with ad overlay */}
            <div className="bg-black px-3 py-1.5 pt-6 flex items-center justify-between" data-id="element-112">
              <span className="text-white text-[10px] font-bold" data-id="element-113">Uber</span>
              <span className="text-white/40 text-[6px]" data-id="element-114">Sponsored</span>
            </div>
            {/* Geo-targeted Takeda ad */}
            <div className="bg-gradient-to-br from-[#2D2926] to-[#E60012] p-3 relative overflow-hidden" data-id="element-115">
              <div className="absolute top-0 right-0 w-12 h-12 bg-[#FF6B6B]/15 rounded-bl-full" data-id="element-116" />
              <div className="flex items-center gap-1.5 mb-1.5" data-id="element-117">
                <div className="bg-white/20 rounded-full px-1.5 py-0.5 flex items-center gap-0.5" data-id="element-118">
                  <MapPin className="w-2 h-2 text-white" data-id="element-119" />
                  <span className="text-white text-[5px] font-medium" data-id="element-120">
                    Near Congress Center
                  </span>
                </div>
              </div>
              <p className="text-white font-bold text-[9px] leading-tight mb-2" data-id="element-121">
                How many UC patients achieve clinical remission?
              </p>
              <div className="space-y-1" data-id="element-122">
                <div className="bg-white/20 border border-white/30 rounded px-2 py-1" data-id="element-123">
                  <span className="text-white text-[7px]" data-id="element-124">
                    Fewer than 1 in 4?
                  </span>
                </div>
                <div className="bg-white/20 border border-white/30 rounded px-2 py-1" data-id="element-125">
                  <span className="text-white text-[7px]" data-id="element-126">About 1 in 3?</span>
                </div>
                <div className="bg-[#FF6B6B]/40 border border-[#FF6B6B] rounded px-2 py-1" data-id="element-127">
                  <span className="text-white text-[7px] font-medium" data-id="element-128">
                    More than 1 in 2? ✓
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2" data-id="element-129">
                <span className="text-white/60 text-[5px]" data-id="element-130">Takeda</span>
                <span className="text-white/30 text-[5px]" data-id="element-131">Ad</span>
              </div>
            </div>
            {/* Bottom ride status */}
            <div className="bg-white px-3 py-1.5 border-t border-gray-100" data-id="element-132">
              <div className="flex items-center justify-between" data-id="element-133">
                <span className="text-[8px] font-medium text-black" data-id="element-134">
                  Your ride is arriving
                </span>
                <span className="text-[8px] font-bold text-black" data-id="element-135">1 min</span>
              </div>
            </div>
          </>}

        {variant === 'engaged' && <>
            {/* Uber header */}
            <div className="bg-black px-3 py-1.5 pt-6 flex items-center justify-between" data-id="element-136">
              <span className="text-white text-[10px] font-bold" data-id="element-137">Uber</span>
              <span className="text-white/40 text-[6px]" data-id="element-138">Sponsored</span>
            </div>
            {/* Result + CTA */}
            <div className="bg-gradient-to-br from-[#2D2926] to-[#E60012] p-3 relative overflow-hidden" data-id="element-139">
              <div className="absolute top-0 right-0 w-10 h-10 bg-[#FF6B6B]/15 rounded-bl-full" data-id="element-140" />
              <div className="text-center mb-2" data-id="element-141">
                <div className="inline-flex items-center gap-1 bg-green-500/20 rounded-full px-2 py-0.5 mb-1.5" data-id="element-142">
                  <span className="text-green-400 text-[8px] font-bold" data-id="element-143">
                    ✓ Correct!
                  </span>
                </div>
                <p className="text-white text-[7px] leading-snug" data-id="element-144">
                  More than 1 in 2 patients achieved clinical remission
                </p>
              </div>
              <div className="bg-white/15 rounded-lg p-2 mb-2" data-id="element-145">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-146">
                  <Trophy className="w-3 h-3 text-[#FF6B6B]" data-id="element-147" />
                  <span className="text-white text-[7px] font-bold" data-id="element-148">
                    Claim your prize!
                  </span>
                </div>
                <p className="text-white/80 text-[6px] leading-snug" data-id="element-149">
                  Visit Booth #412 at the Congress Center
                </p>
              </div>
              <div className="flex justify-center" data-id="element-150">
                <div className="bg-white rounded-full px-3 py-1" data-id="element-151">
                  <span className="text-[#E60012] text-[7px] font-semibold flex items-center gap-1" data-id="element-152">
                    <Navigation className="w-2 h-2" data-id="element-153" /> Get Directions
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between mt-2" data-id="element-154">
                <span className="text-white/60 text-[5px]" data-id="element-155">Takeda</span>
                <span className="text-white/30 text-[5px]" data-id="element-156">Ad</span>
              </div>
            </div>
            {/* Bottom */}
            <div className="bg-white px-3 py-1.5 border-t border-gray-100" data-id="element-157">
              <div className="flex items-center justify-between" data-id="element-158">
                <span className="text-[8px] font-medium text-black" data-id="element-159">
                  You've arrived
                </span>
                <MapPin className="w-3 h-3 text-green-500" data-id="element-160" />
              </div>
            </div>
          </>}
      </div>
    </div>;
}
export function SlideArrival() {
  const [imgError2, setImgError2] = useState(false);
  return <div className="h-full w-full bg-[#FDF8F8] text-[#2D2926] p-6 md:p-10 flex flex-col" data-id="element-161">
      <JourneyTimeline currentStage={2} data-id="element-162" />

      <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="mb-3 border-l-4 border-[#E8384F] pl-5" data-id="element-163">
        <div className="flex items-center gap-3 mb-1" data-id="element-164">
          <span className="text-2xl" data-id="element-165">🧲</span>
          <h2 className="text-2xl font-bold text-[#2D2926]" data-id="element-166">
            Arrival & Vicinity Activation
          </h2>
        </div>
        <p className="text-base text-[#E8384F] font-medium" data-id="element-167">
          Goal: Capture attention before they reach the congress floor
        </p>
      </motion.div>

      <div className="flex-1 grid grid-cols-1 md:grid-cols-[0.8fr_1.2fr] gap-5 min-h-0" data-id="element-168">
        {/* Left Column */}
        <div className="flex flex-col gap-3 overflow-auto" data-id="element-169">
          <motion.div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100" variants={container} initial="hidden" animate="show" data-id="element-170">
            <h3 className="text-sm font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-171">
              <span className="w-6 h-1 bg-[#E8384F] rounded-full" data-id="element-172"></span>
              Tactics
            </h3>
            <ul className="space-y-2.5" data-id="element-173">
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-174">
                <MapPin className="w-4 h-4 text-[#E8384F] mt-0.5 shrink-0" data-id="element-175" />
                <span className="text-gray-700 text-sm" data-id="element-176">
                  <strong className="text-[#2D2926]" data-id="element-177">Geo-targeted ads</strong>{' '}
                  inside Uber/ride-share apps near congress venue
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-178">
                <Bus className="w-4 h-4 text-[#E8384F] mt-0.5 shrink-0" data-id="element-179" />
                <span className="text-gray-700 text-sm" data-id="element-180">
                  Bus stop/outdoor ads with QR codes
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-181">
                <Tablet className="w-4 h-4 text-[#E8384F] mt-0.5 shrink-0" data-id="element-182" />
                <span className="text-gray-700 text-sm" data-id="element-183">
                  Rep-activated iPads: "Ready for a challenge?"
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-184">
                <Gamepad2 className="w-4 h-4 text-[#E8384F] mt-0.5 shrink-0" data-id="element-185" />
                <span className="text-gray-700 text-sm" data-id="element-186">
                  Mini-game or peer poll to spark curiosity
                </span>
              </motion.li>
            </ul>
          </motion.div>

          <motion.div className="bg-[#E8384F]/5 p-4 rounded-xl border border-[#E8384F]/10" variants={container} initial="hidden" animate="show" data-id="element-187">
            <h3 className="text-sm font-bold text-[#2D2926] mb-3 flex items-center gap-2" data-id="element-188">
              <span className="w-6 h-1 bg-[#FF6B6B] rounded-full" data-id="element-189"></span>
              Snakker™ Features
            </h3>
            <ul className="space-y-2.5" data-id="element-190">
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-191">
                <div className="w-2 h-2 rounded-full bg-[#FF6B6B] mt-1.5 shrink-0" data-id="element-192" />
                <span className="text-gray-700 text-sm" data-id="element-193">
                  Teaser Snaks™ with{' '}
                  <strong className="text-[#2D2926]" data-id="element-194">
                    Multiple Select Cue™
                  </strong>
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-195">
                <div className="w-2 h-2 rounded-full bg-[#FF6B6B] mt-1.5 shrink-0" data-id="element-196" />
                <span className="text-gray-700 text-sm" data-id="element-197">
                  QR-based{' '}
                  <strong className="text-[#2D2926]" data-id="element-198">
                    scan-to-engage analytics
                  </strong>
                </span>
              </motion.li>
              <motion.li variants={item} className="flex items-start gap-2.5" data-id="element-199">
                <div className="w-2 h-2 rounded-full bg-[#FF6B6B] mt-1.5 shrink-0" data-id="element-200" />
                <span className="text-gray-700 text-sm" data-id="element-201">
                  Seamless CTA to drive footfall to booth
                </span>
              </motion.li>
            </ul>
          </motion.div>
        </div>

        {/* Right Column — Uber geo-targeted ad flow */}
        <motion.div className="flex flex-col items-center justify-center min-h-0" initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5,
        delay: 0.3
      }} data-id="element-202">
          {/* Flow labels */}
          <motion.div className="flex items-center gap-2 mb-4 bg-white rounded-full px-4 py-1.5 shadow-sm border border-gray-100" initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} data-id="element-203">
            <Car className="w-4 h-4 text-black" data-id="element-204" />
            <span className="text-xs font-bold text-[#2D2926]" data-id="element-205">
              Uber Ride-Share Ad Flow
            </span>
            <MapPin className="w-4 h-4 text-[#E60012]" data-id="element-206" />
          </motion.div>

          {/* Phone mockups with step labels */}
          <div className="flex items-end gap-3 justify-center" data-id="element-207">
            <motion.div className="flex flex-col items-center gap-2" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4
          }} data-id="element-208">
              <span className="text-[10px] font-bold text-[#2D2926] bg-gray-100 rounded-full px-2.5 py-0.5" data-id="element-209">
                ① Waiting for ride
              </span>
              <UberPhoneMockup variant="waiting" data-id="element-210" />
            </motion.div>

            {/* Arrow */}
            <motion.div className="mb-24 text-[#E8384F]" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.6
          }} data-id="element-211">
              <span className="text-lg font-bold" data-id="element-212">→</span>
            </motion.div>

            <motion.div className="flex flex-col items-center gap-2" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.55
          }} data-id="element-213">
              <span className="text-[10px] font-bold text-[#E60012] bg-[#E60012]/10 rounded-full px-2.5 py-0.5" data-id="element-214">
                ② Geo-targeted ad
              </span>
              <UberPhoneMockup variant="ad" data-id="element-215" />
            </motion.div>

            {/* Arrow */}
            <motion.div className="mb-24 text-[#E8384F]" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.75
          }} data-id="element-216">
              <span className="text-lg font-bold" data-id="element-217">→</span>
            </motion.div>

            <motion.div className="flex flex-col items-center gap-2" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7
          }} data-id="element-218">
              <span className="text-[10px] font-bold text-green-700 bg-green-50 rounded-full px-2.5 py-0.5" data-id="element-219">
                ③ Booth visit CTA
              </span>
              <UberPhoneMockup variant="engaged" data-id="element-220" />
            </motion.div>
          </div>

          <motion.p className="text-xs text-gray-500 italic text-center mt-3" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          delay: 0.9
        }} data-id="element-221">
            Doctors see Takeda quiz ads in Uber while riding to the congress
            venue
          </motion.p>
        </motion.div>
      </div>
    </div>;
}